import React, { useState } from 'react';
import { ShoppingCart } from 'lucide-react';
import { getSupabase } from '../services/supabase';
import useLocalStorage from '../hooks/useLocalStorage';
import { getRankTone } from '../config/tiers';

const pickMostUsedStore = (counts) => {
  const entries = Object.entries(counts || {});
  if (entries.length === 0) return '';
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
};

const ShoppingList = ({ shoppingList, addToShoppingList, removeFromShoppingList, clearShoppingList, user, tier }) => {
  const [itemName, setItemName] = useState('');
  const [quantity, setQuantity] = useState('');

  const [priceData, setPriceData] = useState(null);
  const [storeTotals, setStoreTotals] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceZip, setPriceZip] = useLocalStorage('shoppingList.zip', '');

  const [storeCounts, setStoreCounts] = useLocalStorage('shoppingList.storeCounts', {});

  const [reportOpen, setReportOpen] = useState(false);
  const [reportItem, setReportItem] = useState('');
  const [reportStore, setReportStore] = useState(() => pickMostUsedStore(JSON.parse(localStorage.getItem('shoppingList.storeCounts') || '{}')));
  const [reportPrice, setReportPrice] = useState('');
  const [reportUnit, setReportUnit] = useState('each');
  const [reportQty, setReportQty] = useState('1');
  const [reportStatus, setReportStatus] = useState('');

  const addItem = () => {
    if (itemName.trim() && quantity) {
      addToShoppingList(itemName, parseInt(quantity) || 1);
      setItemName('');
      setQuantity('');
    }
  };

  const comparePrices = async () => {
    if (shoppingList.length === 0) return;
    setPriceLoading(true);
    setPriceData(null);
    setStoreTotals(null);

    const items = shoppingList.map((i) => i.name);
    const zip = priceZip.trim() || null;

    try {
      const db = await getSupabase();
      const [compRes, totalRes] = await Promise.all([
        db.rpc('get_price_comparison', { p_items: items, p_zip: zip }),
        db.rpc('get_store_totals', { p_items: items, p_zip: zip }),
      ]);

      if (compRes.data) setPriceData(compRes.data);
      if (totalRes.data) setStoreTotals(totalRes.data);
    } catch {
      // Silently fail — UI shows empty state
    }
    setPriceLoading(false);
  };

  const submitReport = async () => {
    if (!reportItem.trim() || !reportStore.trim() || !reportPrice) return;
    setReportStatus('saving');

    try {
      const db = await getSupabase();
      const { data: { session } } = await db.auth.getSession();
      if (!session) {
        setReportStatus('Sign in to report prices.');
        return;
      }

      const res = await fetch('/.netlify/functions/submit-price', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          item_name: reportItem.trim(),
          store_name: reportStore.trim(),
          price: parseFloat(reportPrice),
          unit: reportUnit,
          quantity: parseFloat(reportQty) || 1,
          zip_code: priceZip.trim() || null,
        }),
      });

      if (res.ok) {
        setReportStatus('Price reported — thank you.');
        const usedStore = reportStore.trim();
        const updatedCounts = { ...storeCounts, [usedStore]: (storeCounts[usedStore] || 0) + 1 };
        setStoreCounts(updatedCounts);
        setReportItem('');
        setReportStore(pickMostUsedStore(updatedCounts));
        setReportPrice('');
        setReportQty('1');
        setTimeout(() => setReportStatus(''), 3000);
      } else {
        const err = await res.json();
        setReportStatus(err.error || 'Failed to report.');
      }
    } catch {
      setReportStatus('Network error.');
    }
  };

  const groupByItem = (data) => {
    if (!data) return {};
    return data.reduce((acc, row) => {
      if (!acc[row.item_name]) acc[row.item_name] = [];
      acc[row.item_name].push(row);
      return acc;
    }, {});
  };

  const grouped = groupByItem(priceData);

  return (
    <div className="page-container">
      <h1 className="page-title">Shopping List</h1>

      <div className="form-row">
        <div className="form-group" style={{ flex: 2 }}>
          <label htmlFor="shop-name" className="form-label">Item Name</label>
          <input
            id="shop-name"
            type="text"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            placeholder="e.g. Milk"
            className="form-input"
            onKeyDown={(e) => e.key === 'Enter' && addItem()}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label htmlFor="shop-qty" className="form-label">Quantity</label>
          <input
            id="shop-qty"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="1"
            className="form-input"
            onKeyDown={(e) => e.key === 'Enter' && addItem()}
          />
        </div>
      </div>

      <div className="btn-group" style={{ marginBottom: '24px' }}>
        <button onClick={addItem} className="btn btn-primary" style={{ flex: 1 }}>
          Add Item
        </button>
        <button
          onClick={clearShoppingList}
          className="btn btn-danger"
          disabled={shoppingList.length === 0}
        >
          Clear All
        </button>
      </div>

      <div className="catalog-section" style={{ marginTop: '16px' }}>
        <div className="catalog-header">
          <h2 className="catalog-title">Items</h2>
          <span className="catalog-count">{shoppingList.length} items</span>
        </div>

        {shoppingList.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><ShoppingCart size={40} aria-hidden="true" /></div>
            <h3 className="empty-state-heading">Nothing to buy yet</h3>
            <p className="empty-state-text">{getRankTone(tier, 'shoppingEmpty')}</p>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {shoppingList.map((item, index) => (
              <li key={index} className="list-item">
                <span className="list-item-name">{item.name}</span>
                <span className="list-item-qty">x{item.quantity}</span>
                <button
                  onClick={() => removeFromShoppingList(index)}
                  className="btn btn-danger btn-sm"
                  aria-label={`Remove ${item.name}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Price Finder */}
      {shoppingList.length > 0 && (
        <div className="settings-section" style={{ marginTop: '24px' }}>
          <h2 className="settings-section-title">Price Finder</h2>
          <p className="settings-description">
            Compare crowdsourced prices across stores for your list items.
          </p>

          <div className="form-row" style={{ alignItems: 'flex-end' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="price-zip" className="form-label">ZIP Code (optional)</label>
              <input
                id="price-zip"
                type="text"
                value={priceZip}
                onChange={(e) => setPriceZip(e.target.value)}
                placeholder="e.g. 90210"
                className="form-input"
                maxLength={10}
              />
            </div>
            <button
              onClick={comparePrices}
              className="btn btn-primary"
              disabled={priceLoading}
              style={{ marginBottom: '16px' }}
            >
              {priceLoading ? 'Checking...' : 'Compare Prices'}
            </button>
          </div>

          {/* Store Totals */}
          {storeTotals && storeTotals.length > 0 && (
            <div className="price-totals">
              <h3 className="price-totals-title">Estimated Total by Store</h3>
              {storeTotals.map((st, i) => (
                <div key={st.store_name} className={`price-total-row${i === 0 ? ' is-cheapest' : ''}`}>
                  <div className="price-total-store">
                    <span className="price-total-name">{st.store_name}</span>
                    <span className="price-total-coverage">
                      {st.items_found}/{Number(st.items_found) + Number(st.items_missing)} items
                    </span>
                  </div>
                  <span className="price-total-amount">${Number(st.total_cost).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Per-Item Breakdown */}
          {priceData && priceData.length > 0 && (
            <div className="price-breakdown">
              <h3 className="price-breakdown-title">Per-Item Breakdown</h3>
              {Object.entries(grouped).map(([item, stores]) => (
                <div key={item} className="price-item-group">
                  <h4 className="price-item-name">{item}</h4>
                  {stores.map((s) => (
                    <div key={s.store_name} className="price-item-row">
                      <span className="price-item-store">{s.store_name}</span>
                      <div className="price-item-right">
                        <span className="price-item-amount">
                          ${Number(s.median_price).toFixed(2)}/{s.unit}
                        </span>
                        <span className="price-item-reports">
                          {s.report_count} report{Number(s.report_count) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {priceData && priceData.length === 0 && !priceLoading && (
            <div className="empty-state" style={{ marginTop: '12px' }}>
              <p className="empty-state-text">No price data yet for these items. Be the first to report!</p>
            </div>
          )}
        </div>
      )}

      {/* Report a Price */}
      {user && (
        <div className="settings-section" style={{ marginTop: '24px' }}>
          <div
            className="price-report-toggle"
            onClick={() => setReportOpen(!reportOpen)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setReportOpen(!reportOpen)}
          >
            <h2 className="settings-section-title" style={{ margin: 0 }}>Report a Price</h2>
            <span className="price-report-arrow">{reportOpen ? '▲' : '▼'}</span>
          </div>

          {reportOpen && (
            <div style={{ marginTop: '12px' }}>
              <p className="settings-description">
                Help the community by sharing what you paid.
              </p>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="rpt-item" className="form-label">Item</label>
                  <input
                    id="rpt-item"
                    type="text"
                    value={reportItem}
                    onChange={(e) => setReportItem(e.target.value)}
                    placeholder="e.g. Whole Milk"
                    className="form-input"
                  />
                </div>
                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="rpt-store" className="form-label">Store</label>
                  <input
                    id="rpt-store"
                    type="text"
                    value={reportStore}
                    onChange={(e) => setReportStore(e.target.value)}
                    placeholder="e.g. Trader Joe's"
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="rpt-price" className="form-label">Price ($)</label>
                  <input
                    id="rpt-price"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={reportPrice}
                    onChange={(e) => setReportPrice(e.target.value)}
                    placeholder="3.99"
                    className="form-input"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="rpt-qty" className="form-label">Qty</label>
                  <input
                    id="rpt-qty"
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={reportQty}
                    onChange={(e) => setReportQty(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="rpt-unit" className="form-label">Unit</label>
                  <select
                    id="rpt-unit"
                    value={reportUnit}
                    onChange={(e) => setReportUnit(e.target.value)}
                    className="form-select"
                  >
                    <option value="each">each</option>
                    <option value="lb">lb</option>
                    <option value="oz">oz</option>
                    <option value="gal">gal</option>
                    <option value="qt">qt</option>
                    <option value="dozen">dozen</option>
                    <option value="bunch">bunch</option>
                  </select>
                </div>
              </div>
              <button
                onClick={submitReport}
                className="btn btn-primary btn-block"
                disabled={reportStatus === 'saving'}
              >
                {reportStatus === 'saving' ? 'Saving...' : 'Submit Price'}
              </button>
              {reportStatus && reportStatus !== 'saving' && (
                <p className={`report-status ${reportStatus.includes('thank') ? 'report-status--success' : 'report-status--error'}`}>
                  {reportStatus}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ShoppingList;
